import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/* AJ-FREEZE-FIX (2026-10-26): Booster Currency spawns a fresh 800 ms interval
 * on every shopper input event; during option tapping these pile up and churn
 * the page. Theme-side guard only (the app itself is untouched): collapse
 * Booster's 800 ms interval spawns to a single live instance. */
if (typeof window !== 'undefined' && !window.__ajBoosterGate) {
  window.__ajBoosterGate = true;
  const ajNativeSetInterval = window.setInterval.bind(window);
  const ajNativeClearInterval = window.clearInterval.bind(window);
  let ajBoosterTimer = null;
  window.setInterval = function (handler, delay, ...args) {
    if (delay === 800) {
      let fromBooster = false;
      try {
        fromBooster = (new Error().stack || '').includes('booster_currency');
      } catch (e) {}
      if (fromBooster) {
        if (ajBoosterTimer !== null) return ajBoosterTimer;
        ajBoosterTimer = ajNativeSetInterval(handler, delay, ...args);
        return ajBoosterTimer;
      }
    }
    return ajNativeSetInterval(handler, delay, ...args);
  };
  window.clearInterval = function (id) {
    if (id === ajBoosterTimer) ajBoosterTimer = null;
    return ajNativeClearInterval(id);
  };
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => {
    if (!this.#ajCssFrozen) this.updateVariantPickerCss();
  });

  /* AJ-FREEZE-FIX: local variant resolution state */
  /** @type {any[] | null | undefined} parsed data-aj-variants payload (undefined = not parsed yet) */
  #ajData = undefined;
  /** @type {any} latest locally resolved variant awaiting the debounced apply */
  #ajPendingVariant = null;
  /** @type {number | undefined} */
  #ajLocalTimer = undefined;
  /** @type {boolean} true once pill measurements are cached; per-change layout reads stop */
  #ajCssFrozen = false;
  /** @type {(number | undefined)[][] | null} cached pill widths per fieldset/input, measured once */
  #ajWidthCache = null;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    /* AJ-FREEZE-FIX: measure every pill once now, serve later updates from the
     * cache, and never run forced-layout measurements on option changes. */
    this.#ajCacheWidths();
    this.#ajApplyCachedMeasurements();
    this.#ajCssFrozen = true;
    let ajResizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(ajResizeTimer);
      ajResizeTimer = setTimeout(() => {
        if (!this.isConnected) return;
        this.#ajCacheWidths();
        this.#ajApplyCachedMeasurements();
      }, 200);
    });

    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    /* AJ-FREEZE-FIX: on the standard product template, resolve the variant from
     * the page's embedded variant data and update the buy box in place — no
     * per-change fetch, full-page parse or picker morph. Any case the local
     * resolver cannot prove (combined listings, featured sections, select-style
     * options, per-variant gallery media) keeps the original network path. */
    const ajLocalVariant =
      isOnProductPage && !loadsNewProduct && !morphElementSelector && event.target.closest('fieldset')
        ? this.#ajResolveVariant()
        : null;

    if (ajLocalVariant) {
      // The clicked input's rendered data-variant-id describes the initial
      // option combination; point it at the actually resolved variant so the
      // URL update below (and anything reading it) stays correct.
      selectedOption.dataset.variantId = String(ajLocalVariant.id);
      this.#ajScheduleLocalUpdate(ajLocalVariant);
    } else {
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);
    }

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /* ================= AJ-FREEZE-FIX: local variant resolution =================
   * The standard product template embeds every variant of the product in a
   * `script[data-aj-variants]` payload (see variant-main-picker). Option
   * changes are resolved against that data and applied directly to the live
   * buy box; the VariantUpdateEvent contract is preserved by dispatching the
   * event with small locally built fragments instead of a fetched page.
   */

  /**
   * Measures every pill width once and caches it per fieldset/input.
   */
  #ajCacheWidths() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#ajWidthCache = fieldsets.map((_, fieldsetIndex) => {
      const radios = this.#radios[fieldsetIndex] || [];
      return radios.map((radio) => radio.parentElement?.offsetWidth || undefined);
    });
  }

  /**
   * Applies pill CSS variables from the width cache — no layout reads.
   * @param {number} [onlyFieldsetIndex] - Restrict to one fieldset.
   */
  #ajApplyCachedMeasurements(onlyFieldsetIndex) {
    if (!this.#ajWidthCache) return;
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    const applyOne = (fieldsetIndex) => {
      const fieldset = fieldsets[fieldsetIndex];
      const checkedIndices = this.#checkedIndices[fieldsetIndex];
      const widths = this.#ajWidthCache?.[fieldsetIndex];
      if (!fieldset || !checkedIndices || !widths) return;

      const widthFor = (/** @type {number | undefined} */ inputIndex) => {
        if (inputIndex === undefined) return undefined;
        let width = widths[inputIndex];
        if (!width) {
          // Options hidden at init (e.g. other metal families) cache as
          // undefined; measure that single pill the first time it is shown.
          const label = (this.#radios[fieldsetIndex] || [])[inputIndex]?.parentElement;
          if (label && label.getClientRects().length) {
            width = label.offsetWidth || undefined;
            if (width) widths[inputIndex] = width;
          }
        }
        return width;
      };

      const [currentIndex, previousIndex] = checkedIndices;
      this.#applyFieldsetMeasurements({
        fieldset,
        currentIndex,
        previousIndex,
        currentWidth: widthFor(currentIndex),
        previousWidth: widthFor(previousIndex),
      });
    };

    if (onlyFieldsetIndex === undefined) {
      for (let index = 0; index < fieldsets.length; index++) applyOne(index);
    } else {
      applyOne(onlyFieldsetIndex);
    }
  }

  /**
   * Parses (once) and validates the embedded variant payload. The local path
   * is only offered while every variant shares one featured media, so the
   * gallery never needs a per-variant re-render.
   * @returns {any[] | null}
   */
  #ajLocalData() {
    if (this.#ajData !== undefined) return this.#ajData;
    this.#ajData = null;
    const script = this.querySelector('script[data-aj-variants]');
    if (!script?.textContent) return null;
    try {
      const parsed = JSON.parse(script.textContent);
      if (Array.isArray(parsed) && parsed.length && parsed.every((v) => v && Array.isArray(v.o))) {
        const mediaKey = (/** @type {any} */ v) => (v.fm ? String(v.fm.id) : '');
        const first = mediaKey(parsed[0]);
        if (parsed.every((v) => mediaKey(v) === first)) this.#ajData = parsed;
      }
    } catch (e) {
      this.#ajData = null;
    }
    return this.#ajData;
  }

  /**
   * Resolves the currently selected option values to a variant, or null when
   * the selection cannot be proven locally (caller keeps the network path).
   * @returns {any | null}
   */
  #ajResolveVariant() {
    const data = this.#ajLocalData();
    if (!data) return null;
    // Select-style options are not resolved locally.
    if (this.querySelector('select')) return null;

    /** @type {(string | undefined)[]} */
    const values = [];
    let complete = true;
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset) => {
      const productIndex = Number.parseInt(fieldset?.dataset.fieldsetIndex || '');
      const checked = fieldset?.querySelector('input:checked');
      if (Number.isNaN(productIndex) || !checked) {
        complete = false;
        return;
      }
      values[productIndex] = checked.value;
    });

    const optionCount = data[0]?.o.length ?? 0;
    if (!complete || values.length !== optionCount || values.some((v) => v === undefined)) return null;

    return data.find((v) => v.o.every((value, i) => value === values[i])) ?? null;
  }

  /**
   * Trailing 200 ms debounce: a burst of taps resolves once, to the final
   * selection.
   * @param {any} variant
   */
  #ajScheduleLocalUpdate(variant) {
    this.#ajPendingVariant = variant;
    clearTimeout(this.#ajLocalTimer);
    this.#ajLocalTimer = setTimeout(() => this.#ajApplyLocalUpdate(), 200);
  }

  /**
   * Applies the pending locally resolved variant to the live buy box and
   * dispatches the standard variant-update event for existing listeners
   * (price, product form, SKU, apps).
   */
  #ajApplyLocalUpdate() {
    const v = this.#ajPendingVariant;
    if (!v) return;

    const resource = {
      id: v.id,
      title: v.o.join(' / '),
      options: v.o,
      price: v.price,
      compare_at_price: v.cap || null,
      available: v.av,
      sku: v.sku ?? null,
      featured_media: v.fm ? { id: v.fm.id, preview_image: { src: v.fm.src } } : null,
    };

    const section = this.closest('.shopify-section') || document;

    // 1. Keep the embedded selected-variant JSON in sync (SKU/copy readers).
    const variantJson = this.querySelector('script[type="application/json"]:not([data-aj-variants])');
    if (variantJson) {
      variantJson.textContent = JSON.stringify({
        id: v.id,
        title: resource.title,
        option1: v.o[0] ?? null,
        option2: v.o[1] ?? null,
        option3: v.o[2] ?? null,
        price: v.price,
        compare_at_price: v.cap || null,
        available: v.av,
        sku: v.sku ?? null,
        featured_media: resource.featured_media,
      });
    }

    // 2. Legend "selected value" labels (mirrors the server render: the
    //    label's square text when present — e.g. "2.00" — else the raw value).
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset) => {
      const span = fieldset?.querySelector('.variant-selected-value');
      const checked = fieldset?.querySelector('input:checked');
      if (!span || !checked) return;
      const labelText = checked.closest('label')?.querySelector('.variant-square__text')?.textContent?.trim();
      span.textContent = labelText || checked.value;
    });

    // 3. Price, compare-at, discount badge and inline SKU in the live DOM.
    const money = (/** @type {number} */ cents) =>
      '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    section.querySelectorAll('product-price').forEach((pp) => {
      const priceMoney = pp.querySelector('.price .money') || pp.querySelector('[ref="priceContainer"] .money');
      if (priceMoney) priceMoney.textContent = money(v.price);

      const compareMoney = pp.querySelector('.compare-at-price .money');
      const compareGroup = pp.querySelector('.compare-at-price')?.closest('[role="group"]') || compareMoney;
      if (compareMoney && compareGroup) {
        if (v.cap && v.cap > v.price) {
          compareMoney.textContent = money(v.cap);
          compareGroup.style.removeProperty('display');
        } else {
          compareGroup.style.display = 'none';
        }
      }

      const badge = pp.querySelector('.custom-discount-badge');
      if (badge) {
        if (v.cap && v.cap > v.price) {
          badge.textContent = `${Math.round((1 - v.price / v.cap) * 100)}% Off`;
          badge.style.removeProperty('display');
        } else {
          badge.style.display = 'none';
        }
      }

      const sku = pp.querySelector('.product-sku-inline');
      if (sku && v.sku) sku.textContent = `SKU: ${v.sku}`;
    });

    // 4. Shop Pay installment terms key off the variant id.
    section.querySelectorAll('shopify-payment-terms').forEach((el) => el.setAttribute('variant-id', String(v.id)));

    // 5. Gallery: the local path only runs when all variants share one
    //    featured media; if this variant carries it, rest the gallery on it
    //    in place (no re-render).
    if (v.fm && v.fm.id) {
      try {
        const gallery = /** @type {any} */ (section.querySelector('media-gallery'));
        const slides = gallery?.querySelectorAll('slideshow-slide');
        if (gallery && slides?.length && gallery.slideshow?.select) {
          const idx = Array.from(slides).findIndex(
            (s) => s.dataset.mediaId === String(v.fm.id) || s.querySelector(`[data-media-id="${v.fm.id}"]`)
          );
          if (idx > 0) gallery.slideshow.select(idx, undefined, { animate: false });
        }
      } catch (e) {
        /* gallery stays on the shared media */
      }
    }

    // 6. Dispatch the standard update event with locally built fragments
    //    (patched clones of the live price block and product form), so every
    //    existing listener — price, form/ATC state, SKU — updates exactly as
    //    it does on the network path, without a fetch or a picker morph.
    const html = document.createElement('div');
    const priceBlock = section.querySelector('product-price');
    if (priceBlock) html.appendChild(priceBlock.cloneNode(true));
    const formBlock = section.querySelector('product-form-component');
    if (formBlock) html.appendChild(formBlock.cloneNode(true));

    if (this.selectedOptionId) {
      this.dispatchEvent(
        new VariantUpdateEvent(resource, this.selectedOptionId, {
          html,
          productId: this.dataset.productId ?? '',
        })
      );
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    if (this.#ajCssFrozen) {
      this.#ajApplyCachedMeasurements(fieldsetIndex);
      return;
    }
    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          newProduct = this.updateVariantPicker(html);
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#ajCssFrozen = false;
    this.updateVariantPickerCss();
    this.#ajCacheWidths();
    this.#ajCssFrozen = true;

    return newProduct;
  }

  updateVariantPickerCss() {
    if (this.#ajCssFrozen) {
      this.#ajApplyCachedMeasurements();
      return;
    }
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
